'use client';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useRef,
} from 'react';
import {
  FaceLandmarker,
  type FaceLandmarkerResult,
  FilesetResolver,
} from '@mediapipe/tasks-vision';
import type { Coords } from '@witmetrics/utils';
import { isEmpty } from '@witmetrics/utils/misc';
import type { FaceLandmarks, MouthOutlines } from './types';
import { FILESET } from './constants';
import { buildConfig } from './utils';

type FaceLandmarksValue = {
  getImageLandmarker: () => FaceLandmarker | null;
  getVideoLandmarker: () => FaceLandmarker | null;
  initializeImageLandmarker: () => Promise<FaceLandmarker>;
  initializeVideoLandmarker: () => Promise<FaceLandmarker>;
  detectImageLandmarks: (img: HTMLImageElement) => FaceLandmarks;
  detectVideoLandmarks: (
    video: HTMLVideoElement,
    time: number
  ) => FaceLandmarks;
  getMouthOutlines: (faceLandmarks: FaceLandmarks) => MouthOutlines;
};

const FaceLandmarksContext = createContext<FaceLandmarksValue | undefined>(
  undefined
);

export default function FaceLandmarksProvider({ children }: PropsWithChildren) {
  const imageLandmarker = useRef<FaceLandmarker | null>(null);
  const videoLandmarker = useRef<FaceLandmarker | null>(null);

  const getImageLandmarker = () => imageLandmarker.current;

  const getVideoLandmarker = () => videoLandmarker.current;

  const initializeImageLandmarker = async (
    faces = 1
  ): Promise<FaceLandmarker> => {
    imageLandmarker.current = null;
    const resolver = await FilesetResolver.forVisionTasks(FILESET);
    imageLandmarker.current = await FaceLandmarker.createFromOptions(
      resolver,
      buildConfig('IMAGE', faces)
    );
    return imageLandmarker.current;
  };

  const initializeVideoLandmarker = async (
    faces = 1
  ): Promise<FaceLandmarker> => {
    videoLandmarker.current = null;
    const resolver = await FilesetResolver.forVisionTasks(FILESET);
    videoLandmarker.current = await FaceLandmarker.createFromOptions(
      resolver,
      buildConfig('VIDEO', faces)
    );
    return videoLandmarker.current;
  };

  const detectImageLandmarks = (
    img: TexImageSource
  ): FaceLandmarkerResult['faceLandmarks'] => {
    if (!imageLandmarker.current) {
      throw Error(
        'FaceLandmarkProvider: Attempting to call detectImageLandmarks when imageLandmarker not initialized'
      );
    }
    return imageLandmarker.current.detect(img).faceLandmarks;
  };

  const detectVideoLandmarks = (
    videoEl: HTMLVideoElement,
    time: number
  ): FaceLandmarkerResult['faceLandmarks'] => {
    if (!videoLandmarker.current) {
      throw Error(
        'FaceLandmarkProvider: Attempting to call detectVideoLandmarks when videoLandmarker not initialized'
      );
    }
    return videoLandmarker.current.detectForVideo(videoEl, time).faceLandmarks;
  };

  const getMouthOutlines = (faceLandmarks: FaceLandmarks): MouthOutlines => {
    // Returns an array of coords for each mouth's outline
    let result: MouthOutlines = [];
    if (!faceLandmarks || isEmpty(faceLandmarks)) {
      return [
        [
          [0, 0],
          [0, 0],
        ],
      ];
    }
    faceLandmarks.forEach((landmarks) => {
      if (!landmarks || isEmpty(landmarks)) return;
      let paths: Coords[] = [];
      const upper = FaceLandmarker.FACE_LANDMARKS_LIPS.slice(20, 30);
      const lower = FaceLandmarker.FACE_LANDMARKS_LIPS.slice(30, 40);
      const connections = [...upper, ...lower.reverse()];
      for (let i = 0; i < connections.length; i++) {
        // TODO: Skip if current start === previous end
        const start = landmarks[connections[i].start];
        const end = landmarks[connections[i].end];
        paths.push([start.x, start.y], [end.x, end.y]);
      }
      result.push(paths);
    });
    return result;
  };

  const contextValue = {
    getImageLandmarker,
    getVideoLandmarker,
    initializeImageLandmarker,
    initializeVideoLandmarker,
    detectImageLandmarks,
    detectVideoLandmarks,
    getMouthOutlines,
  };

  return (
    <FaceLandmarksContext.Provider value={contextValue}>
      {children}
    </FaceLandmarksContext.Provider>
  );
}

export function useFaceLandmarks() {
  const context = useContext(FaceLandmarksContext);
  if (!context) {
    throw Error('useFaceLandmarks must be used within a FaceLandmarksProvider');
  }
  return context;
}
